import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const CondolenceHistoryAll = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    fetch('/api/condolence/check', { credentials: 'include' })
      .then(res => { if (!res.ok) navigate('/condolence'); })
      .catch(() => navigate('/condolence'));

    fetch('/api/condolence/applications/all', { credentials: 'include' })
      .then(res => {
        if (res.status === 403) {
          alert('管理者権限が必要です');
          navigate('/condolence/menu');
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data) setApplications(data.applications || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [navigate]);

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const storeOptions = useMemo(() => {
    const seen = new Map();
    applications.forEach(a => {
      const key = `${a.store_id} - ${a.store_name || ''}`;
      if (!seen.has(a.store_id)) seen.set(a.store_id, key);
    });
    return Array.from(seen.entries()).sort();
  }, [applications]);

  const filtered = applications.filter(app => {
    if (storeFilter && String(app.store_id) !== storeFilter) return false;
    if (keyword) {
      const kw = keyword.toLowerCase();
      const hay = [
        app.employee_name, app.deceased_name, app.relationship,
        app.store_name, String(app.store_id), app.employee_category,
      ].join(' ').toLowerCase();
      if (!hay.includes(kw)) return false;
    }
    return true;
  });

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <button className="back-button" onClick={() => navigate('/condolence/menu')}>← 戻る</button>
        <h1>全店舗 提出情報一覧</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">🗂️</span>
          <span className="title" style={{ color: '#37474f' }}>全店舗 提出情報一覧（管理者用）</span>
        </div>

        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: '13px', color: '#666' }}>店舗</label>
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">すべての店舗</option>
                {storeOptions.map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '2 1 300px' }}>
              <label style={{ fontSize: '13px', color: '#666' }}>キーワード（対象社員/故人名/続柄など）</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例: 鈴木"
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>読み込み中...</p>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666' }}>該当データがありません</p>
          ) : (
            <>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                {filtered.length}件 / 全{applications.length}件 ※ 行をクリックすると詳細画面へ
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #37474f' }}>
                      <th style={thStyle}>申請日</th>
                      <th style={thStyle}>店舗</th>
                      <th style={thStyle}>対象社員</th>
                      <th style={thStyle}>区分</th>
                      <th style={thStyle}>故人名</th>
                      <th style={thStyle}>続柄</th>
                      <th style={thStyle}>訃報</th>
                      <th style={thStyle}>弔電</th>
                      <th style={thStyle}>供花</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(app => {
                      const isObituaryDeclined = app.send_obituary === '辞退する';
                      const isTelegramDeclined = app.request_telegram === '辞退する' || app.request_telegram === '辞退';
                      const isFlowerDeclined = app.request_flowers === '辞退する' || app.request_flowers === '辞退';

                      const renderStatus = (declined, done) => {
                        if (done) return <span style={{ color: '#2e7d32', fontWeight: '600' }}>済</span>;
                        if (declined) return <span style={{ color: '#666' }}>無</span>;
                        return <span style={{ color: '#c62828', fontWeight: '600' }}>必要</span>;
                      };

                      return (
                        <tr key={app.id}
                          style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                          onClick={() => navigate(`/condolence/create?id=${app.id}`)}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.background = ''}
                        >
                          <td style={tdStyle}>{formatDate(app.application_date)}</td>
                          <td style={tdStyle}>{app.store_id} {app.store_name || ''}</td>
                          <td style={tdStyle}>{app.employee_name}</td>
                          <td style={tdStyle}>{app.employee_category || '-'}</td>
                          <td style={tdStyle}>{app.deceased_name || '-'}</td>
                          <td style={tdStyle}>{app.relationship || '-'}</td>
                          <td style={tdStyle}>{renderStatus(isObituaryDeclined, app.obituary_sent)}</td>
                          <td style={tdStyle}>{renderStatus(isTelegramDeclined, app.telegram_ordered)}</td>
                          <td style={tdStyle}>{renderStatus(isFlowerDeclined, app.flower_ordered)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '12px 8px', color: '#37474f', fontSize: '14px', fontWeight: '600'
};
const tdStyle = {
  padding: '12px 8px', fontSize: '14px', color: '#333'
};

export default CondolenceHistoryAll;
